import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCircle, Mail, QrCode } from "lucide-react";
import { Button } from "@patient-watch/ui/button";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { useQuery } from "@tanstack/react-query";
import { templateByIdQueryOptions } from "@/features/templates/api/selectTemplateById";
import { isUuid } from "@/services/isUuid";
import Spinner from "@/components/common/Spinner";
import { useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { useInviteCopy } from "@/features/i18n/inviteCopy";
import { templateSettingsForInvite } from "@/features/templates/utils/templateSettingsForInvite";

export const Route = createFileRoute("/_app/templates/complete")({
    component: CompletePage,
    validateSearch: (search) => ({
        template_id: search.template_id as string,
    }),
});

function CompletePage() {
    const copy = useTemplatesCopy();
    const inviteCopy = useInviteCopy();
    const navigate = useNavigate();
    const search = Route.useSearch();
    const { template_id } = search;

    // Fetch the template to confirm it exists
    const { data: template, isLoading } = useQuery({
        ...templateByIdQueryOptions(template_id),
        enabled: isUuid(template_id),
    });

    // If loading, show spinner
    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <Spinner className="w-16 h-16" />
            </div>
        );
    }

    // If template not found, show error
    if (!template) {
        return (
            <div className="flex flex-col mx-auto py-12 max-w-md">
                <div className="flex flex-col flex-1 justify-center items-center gap-y-8 px-4">
                    <div className="space-y-4 text-center">
                        <h1 className="font-semibold text-red-600 text-3xl">{copy.templateNotFoundTitle}</h1>
                        <p className="text-slate-600 text-lg">{copy.templateNotFoundDescription}</p>
                    </div>
                    <fc.BackButton
                        buttonType="button"
                        label={copy.backToTemplates}
                        onClick={() => void navigate({ to: "/templates" } as never)}
                        className="w-auto"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col mx-auto py-12 max-w-md">
            <div className="flex flex-col flex-1 justify-center items-center gap-y-8 px-4">
                <div className="space-y-4 text-center">
                    <h1 className="bg-clip-text bg-gradient-to-r from-sky-600 to-pink-600 font-semibold text-transparent text-3xl">
                        {copy.templateCreatedTitle}
                    </h1>
                    <p className="text-slate-600 text-lg">{copy.templateCreatedMessage}</p>
                </div>

                <div className="relative rounded-[1rem] w-40 aspect-square">
                    <div className="absolute inset-0 bg-gradient-to-br from-pink-300/40 via-indigo-300/40 to-sky-300/40 shadow-lg border border-white/20 rounded-[1rem]" />
                    <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-transparent rounded-[1rem]" />
                    <div className="absolute inset-0 flex justify-center items-center">
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/30 to-pink-500/30 blur-xl" />
                            <CheckCircle className="relative drop-shadow-lg w-16 h-16 text-indigo-600" />
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-4 pt-4 w-full max-w-md">
                    <Button
                        size="lg"
                        type="button"
                        data-testid="invite-by-email-button"
                        onClick={() =>
                            navigate({
                                to: "/invite/diary/review",
                                search: {
                                    template: template_id,
                                    ...(template.settings &&
                                    typeof template.settings === "object" &&
                                    !Array.isArray(template.settings)
                                        ? templateSettingsForInvite(template.settings as Record<string, unknown>)
                                        : {}),
                                },
                            })
                        }
                        className="group flex justify-center items-center gap-2 w-full"
                    >
                        <Mail className="w-5 h-5" />
                        {inviteCopy.inviteByEmail}
                    </Button>

                    <Button
                        size="lg"
                        type="button"
                        data-testid="share-qr-code-button"
                        onClick={() =>
                            navigate({
                                to: "/invite/diary/qr",
                                search: {
                                    template: template_id,
                                    ...(template.settings as any),
                                },
                            })
                        }
                        variant="secondary"
                        className="group flex justify-center items-center gap-2 w-full"
                    >
                        <QrCode className="w-5 h-5" />
                        {inviteCopy.shareQrCode}
                    </Button>

                    <fc.BackButton
                        variant="link"
                        size="lg"
                        buttonType="button"
                        label={copy.backToTemplates}
                        onClick={() => void navigate({ to: "/templates" } as never)}
                        className="group flex justify-center items-center gap-2 w-full"
                        data-testid="back-to-templates-button"
                    />
                </div>
            </div>
        </div>
    );
}
